import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { View, Platform, Animated, Easing } from 'react-native';
import { TabView } from './TabView';
import { component } from '../../Decorators/Component';
import { CoreConfig } from './../../../CoreConfig';
import { Configuration } from './../../../Domain/DTOs/Configuration/ConfigurationDTO';
import { getIsLandscape, getScreenOffset } from '../../Hooks/UseOrientation';
import { getColorScheme } from '../../Hooks/UseColorScheme';
import { useColorSchemeContext } from '../../Contexts/ThemeContext';
import { useDirection } from '../../Hooks/UseDirection';
export const TabsPanelView = component({
    provider: CoreConfig.provider,
    moduleName: CoreConfig.moduleName,
    componentName: Configuration.ComponentNames.tabsPanel,
    style(theme, mediaQuery, window, props) {
        return {
            container: {
                flex: 1
            },
            tabsContainer: Object.assign({ minHeight: 34, flexDirection: 'row', justifyContent: 'center' }, getIsLandscape().value && {
                paddingLeft: getScreenOffset().value.left,
                paddingRight: getScreenOffset().value.right
            }),
            separator: Object.assign(Object.assign({ backgroundColor: '#e4e6ea', height: 1, marginTop: -1, zIndex: -1, display: 'none' }, mediaQuery.md({
                display: 'flex'
            })), props.leftAlign && {
                display: 'flex'
            }),
            mobileSeparator: Object.assign(Object.assign({ backgroundColor: '#e4e6ea', height: 1, marginTop: -1, zIndex: -1, display: 'flex' }, mediaQuery.md({
                display: 'none'
            })), props.leftAlign && {
                display: 'none'
            }),
            viewContainer: Object.assign({ flex: 1, overflow: 'hidden', marginTop: -1 }, Platform.select({
                web: {
                    marginTop: 0
                }
            })),
            tab: Object.assign({ flex: 1 }, !props.fullWidthTab && mediaQuery.md({
                flex: undefined
            }))
        };
    }
})(({ leftAlign, stayAlive, light, navigationTabs, headerSlot, defaultViewTag, viewChange, tabStyle, tabContainerStyle, style, containerStyle }) => {
    const isFirstRender = useRef(true);
    const [dir] = useDirection();
    const color_scheme_context = useColorSchemeContext();
    const [activeTab, setActiveTab] = useState();
    const [containerWidth, setContainerWidth] = useState(0);
    const slideEffect = useRef(new Animated.Value(0)).current;
    const opacityEffect = useRef(new Animated.Value(0)).current;
    const handlerTabTouch = useCallback((tab) => {
        if (tab.tag === (activeTab === null || activeTab === void 0 ? void 0 : activeTab.tag)) {
            return;
        }
        const useNativeDriver = Platform.OS !== 'web';
        const duration = 0;
        if (isFirstRender.current) {
            isFirstRender.current = false;
            setActiveTab(tab);
            Animated.timing(opacityEffect, { toValue: 1, duration, useNativeDriver }).start();
            Animated.timing(slideEffect, { toValue: 0, duration, useNativeDriver }).start();
            return;
        }
        const findIndexH = (searchTab) => {
            return (tabEl) => {
                return searchTab
                    ? tabEl.tag == searchTab.tag
                    : false;
            };
        };
        const newTabIndex = navigationTabs.findIndex(findIndexH(tab));
        const oldTabIndex = navigationTabs.findIndex(findIndexH(activeTab));
        let slideValue = newTabIndex < oldTabIndex
            ? containerWidth
            : -containerWidth;
        if (dir === 'rtl') {
            slideValue = -slideValue;
        }
        Animated.timing(slideEffect, { toValue: slideValue, duration: 150, easing: Easing.ease, useNativeDriver }).start(() => {
            setActiveTab(tab);
            viewChange === null || viewChange === void 0 ? void 0 : viewChange(tab);
            Animated.sequence([
                Animated.timing(opacityEffect, { toValue: 0, duration, useNativeDriver }),
                Animated.timing(slideEffect, { toValue: -slideValue, duration, useNativeDriver }),
                Animated.timing(opacityEffect, { toValue: 1, duration, useNativeDriver }),
                Animated.timing(slideEffect, { toValue: 0, duration: 150, easing: Easing.ease, useNativeDriver })
            ]).start();
        });
    }, [isFirstRender, viewChange, activeTab, containerWidth, isFirstRender, dir]);
    const handleContainerLayout = useCallback((e) => setContainerWidth(e.nativeEvent.layout.width), []);
    useEffect(() => {
        if (!activeTab && !defaultViewTag) {
            handlerTabTouch(navigationTabs[0]);
            return;
        }
        if ((activeTab === null || activeTab === void 0 ? void 0 : activeTab.tag) !== defaultViewTag) {
            const tab = navigationTabs.find(el => el.tag == defaultViewTag) || navigationTabs[0];
            handlerTabTouch(tab);
        }
    }, [defaultViewTag]);
    const tabContainerStyles = {
        display: 'flex',
        flex: 1,
        overflow: 'hidden',
        opacity: opacityEffect,
        transform: [{ translateX: slideEffect }],
        tabContainerStyle
    };
    let isLight = light || getColorScheme().value.scheme === 'dark' || color_scheme_context.scheme === 'dark';
    return (_jsxs(View, { style: [style === null || style === void 0 ? void 0 : style.container, containerStyle], children: [_jsxs(View, { style: [style === null || style === void 0 ? void 0 : style.tabsContainer, (leftAlign || headerSlot) && { justifyContent: 'flex-start' }], children: [navigationTabs.map(tab => (_jsxs(View, { style: style === null || style === void 0 ? void 0 : style.tab, children: [_jsx(TabView, { light: isLight, value: tab, isActive: tab.tag === (activeTab === null || activeTab === void 0 ? void 0 : activeTab.tag), onPress: handlerTabTouch, containerStyle: tabStyle }), _jsx(View, { style: style === null || style === void 0 ? void 0 : style.mobileSeparator })] }, tab.tag))), headerSlot] }), _jsx(View, { style: [style === null || style === void 0 ? void 0 : style.separator, isLight && { backgroundColor: '#fff' }] }), _jsxs(View, { style: style === null || style === void 0 ? void 0 : style.viewContainer, onLayout: handleContainerLayout, children: [stayAlive && navigationTabs.map(tab => (_jsx(Animated.View, { style: tab.tag === (activeTab === null || activeTab === void 0 ? void 0 : activeTab.tag)
                            ? tabContainerStyles
                            : { display: 'none' }, children: tab.component }, tab.tag))), !stayAlive && activeTab && (_jsx(Animated.View, { style: tabContainerStyles, children: activeTab.component }))] })] }));
});
